import CT from '@marketplace-ai/tags';
import { ColDef } from 'ag-grid-community';
import { CustomCellRendererProps } from 'ag-grid-react';
import { Pill } from '~/components';
import { createDateCol } from '~/components/agGrid/columns/dateColumns';
import { ACTIONS_COLUMN_BASE, ActionsRenderer, type ActionDef } from '~/components/Table/gridUtil';

const TAGS = CT.malpracticeClaims.table;

// Deliberately still on raw palette; the lint rule flags these nine lines.
//
// These hues are intentionally arbitrary: they distinguish statuses without
// implying whether an outcome was good or bad for the provider. Mapping them onto
// the semantic set (success/warning/danger) attaches a favourability axis that the
// data model does not have, and gets it backwards — an `Award` is a judgment
// *against* the provider, while `Withdrawn` means the claimant dropped the claim.
// Green-for-Award / red-for-Withdrawn would mislead a credentialing reviewer
// scanning this grid.
//
// Tokenizing this needs a product decision on whether claim outcomes should be
// colour-coded by severity at all. Until then it stays as-is. See MCV-1500.
const CLAIM_STATUS_STYLES: Record<string, string> = {
  Pending: 'bg-white text-black ring-gray-600/20',
  Settled: 'bg-blue-500 text-white ring-gray-600/20',
  Arbitrated: 'bg-yellow-500 text-white ring-yellow-600/20',
  Award: 'bg-cyan-500 text-white ring-cyan-600/20',
  'In Appeal': 'bg-orange-500 text-white ring-orange-600/20',
  Adjudicated: 'bg-purple-500 text-white ring-purple-600/20',
  Withdrawn: 'bg-red-500 text-white ring-red-600/20',
  // was ring-green-600/20 on a grey chip — copy-paste slip, matched to its siblings
  Dismissed: 'bg-gray-500 text-white ring-gray-600/20',
  Other: 'bg-gray-500 text-white ring-gray-600/20',
};

export const createClaimsColumns = ({
  handleEdit,
  handleDelete,
}: {
  handleEdit: (id: number) => void;
  handleDelete: (id: number) => void;
}): ColDef[] => [
  { field: 'id', hide: true },
  createDateCol('occurrenceDate', 'Occurrence Date'),
  {
    // [MCV-1753] Explicit colId: an id-less column is matched across colDefs rebuilds only by
    // object identity, so it lost its place whenever the page re-rendered.
    colId: 'claimStatus',
    headerName: 'Claim Status',
    // Guard `data?.claimStatus` the same way `valueGetter` does — ag-grid can
    // call cellRenderer with undefined data (pagination/row-animation phases),
    // and while the schema marks claimStatus as required, runtime rows aren't
    // guaranteed to match (legacy data, backend drift). Without the guard an
    // undefined claimStatus throws inside the render pipeline and breaks the
    // whole grid.
    cellRenderer: ({ data }: CustomCellRendererProps) => {
      if (!data?.claimStatus) return null;
      return (
        <Pill
          label={data.claimStatus.label}
          customStyle={CLAIM_STATUS_STYLES[data.claimStatus.value]}
        />
      );
    },
    valueGetter: ({ data }) => data?.claimStatus?.label ?? '',
  },
  {
    headerName: 'Settlement Amount',
    field: 'settlementAmt',
    valueFormatter: ({ value }) =>
      value || value === 0 ? `$${Number(value).toLocaleString()}` : '---',
  },
  {
    headerName: 'Liability Carrier',
    field: 'liabilityCarrier',
  },
  {
    ...ACTIONS_COLUMN_BASE,
    width: 80,
    cellRenderer: ActionsRenderer,
    cellRendererParams: {
      actions: [
        { type: 'edit', handler: handleEdit, cyTag: TAGS.editBtn },
        { type: 'delete', handler: handleDelete, cyTag: TAGS.deleteBtn },
      ] satisfies ActionDef[],
    },
  },
];
